import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import { Badge, Button, Divider, Popover, Tag } from 'antd';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { useSetTikTokReviewStoresStatus, useTikTokReviewStoresStatus } from 'hooks/useTikTokReview';
import { useEffect, useMemo } from 'react';
import { getTikTokGrayStatusShops, TikTokGrayStatusShop } from 'services/api/tiktokReview';
import dayjs from 'lib/dayjs';
import { toNumber } from 'lodash-es';
import { isTiKTok } from 'lib/utils/isTikTok';
import { closeTikTokReviewNotification } from 'components/TikTokReviewNotification';
import toLowerCase from 'utils/toLowerCase';

const Item = (props: TikTokGrayStatusShop) => {
  const { storeName, alias, online, lastActiveTime, region } = props;
  const { t } = useTranslate();

  if (online) {
    return (
      <>
        <div className="flex flex-col gap-1">
          <p title="" className="!mb-0 max-w-80 whitespace-nowrap text-ellipsis overflow-hidden">
            {alias || storeName}
          </p>
          <Badge status="success" text={t('status.online')} />
        </div>
        <Divider className="!my-2" />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-1">
        <p title="" className="!mb-0 max-w-80 whitespace-nowrap text-ellipsis overflow-hidden">
          <Tag color="blue">{t(`region.${toLowerCase(region)}`)}</Tag>
          {alias || storeName}
        </p>
        <Badge status="warning" text={t('status.offline')} />
        {lastActiveTime !== '0' ? (
          <span className="tex-xs text-content-200">
            {t('最后活跃', { time: dayjs(toNumber(lastActiveTime)).format('YYYY-MM-DD HH:mm') })}
          </span>
        ) : null}
      </div>
      <Divider className="!my-2" />
    </>
  );
};

export const TikTokStoreStatus = (props: { platform: Review.Platform }) => {
  const { platform } = props;
  const { t } = useTranslate();
  const shopList = useTikTokReviewStoresStatus();
  const setTiktokReviewStoresStatus = useSetTikTokReviewStoresStatus();

  const { whiteListConfig: { TIKTOK_REVIEW = false } = {} } = useOrgConfigStateValue();

  useRequest(() => getTikTokGrayStatusShops(platform), {
    ready: TIKTOK_REVIEW && isTiKTok(platform),
    refreshDeps: [platform],
    pollingInterval: 30 * 1000,
    onSuccess(shopStatus) {
      setTiktokReviewStoresStatus(shopStatus);
    },
  });

  const hasOfflineShop = useMemo(() => shopList.some(({ online }) => !online), [shopList]);

  useEffect(() => {
    if (!hasOfflineShop) {
      closeTikTokReviewNotification();
    }
  }, [hasOfflineShop]);

  return shopList.length > 0 ? (
    <>
      <div className="px-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span>{t('店铺状态')}</span>
          {hasOfflineShop ? (
            <Badge status="warning" text={t('异常')} />
          ) : (
            <Badge status="success" text={t('正常')} />
          )}
        </div>
        <Popover
          trigger="click"
          placement="rightTop"
          content={
            <div className="max-h-52 overflow-y-auto">
              {shopList.map((item) => (
                <Item key={item.storeId} {...item} />
              ))}
            </div>
          }
        >
          <Button className="!px-0" type="link">
            {t('translation.查看')}
          </Button>
        </Popover>
      </div>
      <Divider className="!m-0" />
    </>
  ) : null;
};
